import {Sound, filters, sound} from '@pixi/sound';
import {
    CoreDI,
    IEventManagerWithAsync, inject,
    injectable,
    IService,
    ResourceLoader,
    SoundMap,
    SystemComponentType
} from '@gongxigames/framework/core';
import {GameConfig, ISoundConfig, SoundConfigMap, SoundProperties, SoundsProperties} from '@gongxigames/framework/slot/types';
import {ResourceManager} from '@gongxigames/framework/slot/services/ResourcesManager';
import {GlobalEventsMap} from '@gongxigames/framework/slot/events';
import {SoundsDuckingConfig} from '@gongxigames/framework/slot/fragments/SoundsDuckingConfig';
import {DI} from '@gongxigames/framework/slot/configs/DI';
import {audioConfig} from '@gongxigames/framework/slot/configs/SoundConfig';

@injectable()
export class SoundManager implements IService {
    public static NAME: symbol = DI.SoundManager;
    public type: SystemComponentType = 'service';

    protected _music: SoundMap = {};
    protected _musicNamesBySource: {[key: string]: string;} = {};
    protected _sounds: SoundMap = {};
    protected _soundsNamesBySource: {[key: string]: string;} = {};
    protected _soundType!: string;
    protected _musicConfig!: ISoundConfig;
    protected _soundConfig!: ISoundConfig;

    @inject(CoreDI.ResourceLoader)
    protected loader!: ResourceLoader;

    @inject(DI.ResourceManager)
    protected _resourceManager!: ResourceManager;

    @inject(CoreDI.IEventManagerWithAsync)
    protected _eventManager!: IEventManagerWithAsync<GlobalEventsMap>;

    @inject(CoreDI.GameConfig)
    protected _gameConfig!: GameConfig;

    public readonly soundsProperties: SoundsProperties = {};

    public init(): void {
        this.setupEvents();
        this.initConfig();

        if (!this._resourceManager.isSoundsLoadingDelayed) {
            this.onDefaultResourcesLoaded();
        }
    }

    public destroy(): void {
        this.removeAllSounds();
    }

    private removeAllSounds(): void {
        sound.stopAll();
        sound.removeAll();
    }

    protected setupEvents(): void {
        this._eventManager.on('General.ResourcesLoading.LOADED_ALL_SOUNDS', this.onAllSoundsLoaded.bind(this), this);
        this._eventManager.on('General.ResourcesLoading.LOADED_ALL_RESOURCES', this.onAllResourcesLoaded.bind(this), this);
        this._eventManager.on('General.ResourcesLoading.LOADED_ALL_RESOURCES', this.onAllResourcesLoaded.bind(this), this);
    }

    protected initConfig(): void {

        const properties = Object.assign(audioConfig.properties || {}, this._gameConfig.audioConfig.properties);

        for (const soundName in properties) {
            if (properties.hasOwnProperty(soundName)) {
                this.soundsProperties[soundName] = properties[soundName];
            }
        }

        this._soundConfig = audioConfig.soundConfig;
        this._musicConfig = audioConfig.musicConfig;

        Object.assign(this._soundConfig.defaultPriority, this._gameConfig.audioConfig.soundConfig.defaultPriority);
        Object.assign(this._soundConfig.afterPriority, this._gameConfig.audioConfig.soundConfig.afterPriority);
        Object.assign(this._musicConfig.defaultPriority, this._gameConfig.audioConfig.musicConfig.defaultPriority);
        Object.assign(this._musicConfig.afterPriority, this._gameConfig.audioConfig.musicConfig.afterPriority);

        this._soundType = this._gameConfig.audioConfig.soundType || audioConfig.soundType;
    }

    protected onDefaultResourcesLoaded(): void {
        this.setAllSounds(this._soundConfig.defaultPriority);
        this.setAllMusic(this._musicConfig.defaultPriority);
    }

    protected onAllResourcesLoaded(): void {
        this.setAllSounds(this._soundConfig.afterPriority);
        this.setAllMusic(this._musicConfig.afterPriority);
    }

    protected onAllSoundsLoaded(): void {
        this.onDefaultResourcesLoaded();
        this.onAllResourcesLoaded();
    }

    protected setAllSounds(sounds: SoundConfigMap): void {
        for (const name in sounds) {
            if (sounds.hasOwnProperty(name)) {
                const soundSource = sounds[name].replace('/soundType/', this._soundType);
                const sound = this.loader.getSound(soundSource);

                this._sounds[name] = sound;
                this._soundsNamesBySource[soundSource] = name;

                if (!this.soundsProperties[name] && sound) {
                    this.soundsProperties[name] = this.getDefaultSoundProperties(sound);
                }

                if (this._sounds[name]) {
                    this.setSoundProperties(sound, name);
                }
            }
        }
    }

    protected setAllMusic(musics: SoundConfigMap): void {
        for (const name in musics) {
            if (musics.hasOwnProperty(name)) {
                const soundSource = musics[name].replace('/soundType/', this._soundType);
                const sound = this.loader.getSound(soundSource);

                this._music[name] = sound;
                this._musicNamesBySource[soundSource] = name;

                if (!this.soundsProperties[name] && sound) {
                    this.soundsProperties[name] = this.getDefaultSoundProperties(sound, true);
                }

                if (sound) {
                    this.setSoundProperties(sound, name);
                }
            }
        }
    }

    protected getDefaultSoundProperties(sound: Sound, isMusic?: boolean): SoundProperties {
        const properties: SoundProperties = {
            volume: sound.volume,
            loop: isMusic || sound.loop,
            panning: 0
        };

        if (sound.filters && sound.filters.length) {
            const stereoFilter = sound.filters.find(filter => filter instanceof filters.StereoFilter);

            properties.panning = stereoFilter ? (stereoFilter as filters.StereoFilter).pan : 0;
        }

        return properties;
    }

    public setSoundProperties(sound: Sound, soundName: string): void {
        const {volume = 1, panning = 0, loop, duck} = this.soundsProperties[soundName] || {};

        sound.volume = volume;
        sound.loop = loop !== undefined ? loop : this.hasMusic(soundName);
        sound.filters = [new filters.StereoFilter(panning)];
        duck && SoundsDuckingConfig.addDuckingConfig(soundName, duck);
    }

    public hasSound(soundID: string): boolean {
        return !!this._sounds[soundID];
    }

    public hasSoundBySource(source: string): boolean {
        return !!this._soundsNamesBySource[source];
    }

    public hasMusic(musicID: string): boolean {
        return !!this._music[musicID];
    }

    public hasMusicBySource(source: string): boolean {
        return !!this._musicNamesBySource[source];
    }

    public getSound(soundID: string): Sound {
        return this._sounds[soundID];
    }

    public getSoundBySource(source: string): Sound {
        const soundID = this._soundsNamesBySource[source];

        return this.getSound(soundID);
    }

    public getMusic(musicID: string): Sound {
        return this._music[musicID];
    }

    public getMusicBySource(source: string): Sound {
        const musicID = this._musicNamesBySource[source];

        return this.getMusic(musicID);
    }

    public setMusic(musicID: string, music: Sound): void {
        this._music[musicID] = music;
    }

    public setMusicBySource(source: string, music: Sound): void {
        const musicID = this._musicNamesBySource[source];

        this.setMusic(musicID, music);
    }

    public setSound(soundID: string, sound: Sound): void {
        this._sounds[soundID] = sound;
    }

    public setSoundBySource(source: string, sound: Sound): void {
        const soundID = this._soundsNamesBySource[source];

        this.setSound(soundID, sound);
    }

    public getSoundIdFromName(name: string): string | undefined {
        for (const sound in this._sounds) {
            if (this._sounds.hasOwnProperty(sound) && this._sounds[sound]) {
                const soundUrl = this._sounds[sound].url;

                if (soundUrl && soundUrl.indexOf(name) >= 0) {
                    return sound;
                }
            }
        }

        for (const music in this._music) {
            if (this._music.hasOwnProperty(music) && this._music[music]) {
                const musicUrl = this._music[music].url;

                if (musicUrl && musicUrl.indexOf(name) >= 0) {
                    return music;
                }
            }
        }
    }

    public getConfigByID(id: string): SoundProperties {
        return this.soundsProperties[id];
    }

    public getSoundKeys(): string[] {
        return Object.keys(this._sounds);
    }

    public getMusicKeys(): string[] {
        return Object.keys(this._music);
    }

    public get audioNamesBySource(): {[key: string]: string;} {
        return {...this._soundsNamesBySource, ...this._musicNamesBySource};
    }
}
